import { IColumn, ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPLink, IPScrollablePane, IPSeparator, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode, IPSelection } from 'baseComponents/Utils/Selection';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import { JSONViewer } from 'components/Utils/JSONViewer';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  deleteIcon,
  getGlobalStackItemStylesPaddingSceneScrollMinHeight,
  globalStackTokensGapMedium,
  selectionIcon,
} from 'globalStyles';
import KPIDataTransform from 'models/kpi/kpiDataTransform';
import KPIDataTransformLog from 'models/kpi/kpiDataTransformLog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiDeleteKPITransformLogs,
  apiGetKPIDataTransformLogs,
  apiReprocessKPITransformLogs,
} from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { getHtmlJSX } from 'utils/html';
import { sortOnDate, sortOnString } from 'utils/sorting';

interface IKPIEvidenceTransformModalProps {
  transform: KPIDataTransform;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (transform: KPIDataTransform) => void;
}

const NotAcceptedEvidenceHistoryModal = (props: IKPIEvidenceTransformModalProps) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(['translation', 'kpis']);
  const [transformLogs, setTransformLogs] = React.useState<KPIDataTransformLog[]>([]);
  const [selectedLogs, setSelectedLogs] = React.useState<KPIDataTransformLog[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showEvidenceModal, setShowEvidenceModal] = React.useState<boolean>(false);
  const [showRemoveLogsDialog, setShowRemoveLogsDialog] = React.useState<boolean>(false);
  const [showConfirmSubmitDialog, setShowConfirmSubmitDialog] = React.useState<boolean>(false);
  const [selection] = useState<IPSelection>(
    new IPSelection({
      selectionMode: IPSelectionMode.multiple,
      onSelectionChanged: () => {
        const selectedItems = selection.getSelection() as KPIDataTransformLog[];
        setSelectedLogs(selectedItems);
      },
    }),
  );

  //
  // Effects
  //
  React.useEffect(() => {
    //react to another KPI
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    try {
      if (!props.transform) return;

      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const logs = await apiGetKPIDataTransformLogs(props.transform.kpiId, accessToken);

      logs.sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return -1 * sortOnDate(a.created, b.created);
      });

      setTransformLogs(logs);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Columns
  //
  const getColumns = (): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'created',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.Created'),
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        isSorted: true,
        isSortedDescending: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item) return;

          return <IPText>{toLocaleDateTimeMedium(item.created)}</IPText>;
        },
      },
      {
        key: 'createdBy',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.CreatedBy'),
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item) return;

          let name: string;
          const user = appContext.globalDataCache.users.get(item.createdById);
          if (user) {
            name = user.name;
          } else {
            name = item.createdBy;
          }

          return <IPPersona imageUrl={user.getPhotoURLObj()} size={IPPersonaSize.size24} text={name}></IPPersona>;
        },
      },
      {
        key: 'evidence',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.Evidence'),
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item || index === undefined) return;

          return (
            <IPLink
              onClick={() => {
                selection.setIndexSelected(index, true, true);
                setShowEvidenceModal(true);
              }}
            >
              {item.resultEvidence}
            </IPLink>
          );
        },
      },
      {
        key: 'errorCode',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.ErrCode'),
        minWidth: 200,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item) return;

          return <IPText>{item.errorCode}</IPText>;
        },
      },
      {
        key: 'errorMsg',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.ErrMsg'),
        minWidth: 100,
        maxWidth: 600,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPTooltipHost content={item.errorMsg}>
              <IPText>{item.errorMsg}</IPText>
            </IPTooltipHost>
          );
        },
      },
      {
        key: 'comment',
        name: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Columns.Comment'),
        minWidth: 150,
        maxWidth: 600,
        isResizable: true,
        onRender: (item?: KPIDataTransformLog, index?: number, column?: IColumn) => {
          if (!item) return;

          return <div>{getHtmlJSX(item.resultComment)}</div>;
        },
      },
    ];

    return allColumns;
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumns());

  //
  // Sorting
  //
  const onColumnClick = (ev: React.MouseEvent<HTMLElement> | undefined, column: IColumn | undefined): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    for (const newCol of newColumns) {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = false;
      }
    }

    const newItems = copyAndSort(transformLogs, currColumn.key, currColumn.isSortedDescending);

    setColumns(newColumns);
    setTransformLogs(newItems);
  };

  const copyAndSort = (
    items: KPIDataTransformLog[],
    columnKey: string,
    isSortedDescending?: boolean,
  ): KPIDataTransformLog[] => {
    if (columnKey === 'createdBy') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        const userA = appContext.globalDataCache.users.get(a.createdById);
        const userB = appContext.globalDataCache.users.get(b.createdById);

        const nameA = userA.name ? userA.name : a.createdBy;
        const nameB = userB.name ? userB.name : b.createdBy;

        return (isSortedDescending ? -1 : 1) * sortOnString(nameA, nameB);
      });
    } else if (columnKey === 'evidence') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.resultEvidence, b.resultEvidence);
      });
    } else if (columnKey === 'comment') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.resultComment, b.resultComment);
      });
    } else if (columnKey === 'errorCode') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.errorCode, b.errorCode);
      });
    } else if (columnKey === 'errorMsg') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.errorMsg, b.errorMsg);
      });
    } else if (columnKey === 'created') {
      return items.slice(0).sort((a: KPIDataTransformLog, b: KPIDataTransformLog) => {
        return (isSortedDescending ? -1 : 1) * sortOnDate(a.created, b.created);
      });
    }

    return items;
  };

  const submitForApproval = () => {
    try {
      const newTransform = props.transform.clone();
      newTransform.linkId = undefined;
      newTransform.evidence = undefined;
      newTransform.rawEvidence = selectedLogs[0].resultEvidence;
      props.onSubmit(newTransform);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const deleteSelectionLogs = async () => {
    try {
      setShowRemoveLogsDialog(false);
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDeleteKPITransformLogs(
        props.transform.kpiId,
        selectedLogs.map((s) => s.kpiDataTransformLogId),
        accessToken,
      );
      selection.setAllSelected(false);
      await loadData();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const reprocessSelectionLogs = async () => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiReprocessKPITransformLogs(
        selectedLogs.map((s) => s.kpiDataTransformLogId),
        accessToken,
      );
      selection.setAllSelected(false);
      await loadData();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCommandBarButtons = (): ICommandBarItemProps[] => {
    const items: ICommandBarItemProps[] = [
      {
        key: 'submit',
        disabled: isLoading || selectedLogs.length !== 1,
        text: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Commands.SubmitForApproval'),
        iconProps: { iconName: 'DocumentApproval' },
        onClick: () => {
          setShowConfirmSubmitDialog(true);
        },
      },
      {
        key: 'selection',
        disabled: isLoading || selectedLogs.length === 0,
        text: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Commands.Selection'),
        iconProps: selectionIcon,
        subMenuProps: {
          items: [
            {
              key: 'reprocess',
              text: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Commands.Reprocess'),
              iconProps: { iconName: 'Redo' },
              onClick: () => {
                reprocessSelectionLogs();
              },
            },
            {
              key: 'div1',
              text: '-',
              itemType: IPContextualMenuItemType.Divider,
            },
            {
              key: 'delete',
              text: t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Commands.Remove'),
              iconProps: deleteIcon,
              onClick: () => {
                setShowRemoveLogsDialog(true);
              },
            },
          ],
        },
      },
    ];

    return items;
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.Title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      minWidth={250}
      width={'80vw'}
      maxWidth={'95vw'}
      minHeight={300}
      height={'70vh'}
      maxHeight={'90vh'}
      hideFooter={false}
    >
      <IPSeparator />
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPText>{t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.SubTitle')}</IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPCommandBar styles={{ root: { padding: 0 } }} items={getCommandBarButtons()}></IPCommandBar>
        </IPStack.Item>
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && (
          <IPStack.Item grow styles={getGlobalStackItemStylesPaddingSceneScrollMinHeight(150)}>
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
              <IPDetailsList
                items={transformLogs}
                columns={columns}
                compact
                layoutMode={IPDetailsListLayoutMode.justified}
                selectionMode={IPSelectionMode.multiple}
                selection={selection}
                isHeaderVisible={true}
                onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
                onColumnHeaderClick={onColumnClick}
              />
            </IPScrollablePane>
          </IPStack.Item>
        )}
      </IPStack>
      <GenericModal
        isOpen={showEvidenceModal}
        title={t('kpi:TabEvidence.Dialogs.Evidence.Title')}
        onClose={() => setShowEvidenceModal(false)}
        minWidth={250}
        minHeight={200}
        width={'50vw'}
        height={'50vh'}
        maxHeight={'95vh'}
        maxWidth={'95vw'}
      >
        <IPSeparator />
        <IPStack verticalFill>
          <ScrollableStackItem>
            <JSONViewer data={selectedLogs[0]?.resultEvidence} />
          </ScrollableStackItem>
        </IPStack>
      </GenericModal>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveLogsDialog(false);
        }}
        onYes={() => {
          deleteSelectionLogs();
        }}
        title={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.DialogRemoveTitle')}
        subText={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.DialogRemoveSubTitle')}
        hidden={!showRemoveLogsDialog}
      />
      <DialogYesNo
        onNo={() => {
          setShowConfirmSubmitDialog(false);
        }}
        onYes={() => {
          submitForApproval();
        }}
        title={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.DialogConfirmSubmitTitle')}
        subText={t('kpi:TabEvidence.Dialogs.NotAcceptedEvidenceHistory.DialogConfirmSubmitSubTitle')}
        hidden={!showConfirmSubmitDialog}
      />
    </GenericModal>
  );
};

export default NotAcceptedEvidenceHistoryModal;
